import React, { useCallback, useEffect, useState } from "react";
import { FormInput } from "@/components/bnp-ui";
import { PropertyService } from "@/services";
import { LoadingSpinner, NewLogoWhite } from "@/components/bnp-icons";
import watermarkLogo from "../../../../../public/assets/images/watermarkLogo.png";
import { toast } from "react-toastify";
import { isVideoFile } from "@/lib/media";

interface FileUploadProps {
  maxFiles?: number;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  onFilesSelected?: (files: File[]) => void;
  onUploadComplete?: (urls: string[]) => void;
  additionalInfo?: any;
  additionalInfoView?: boolean;
  setAdditionalInfo?: any;
  uploadedUrls: string[];
  setUploadedUrls: any;
  setValue?: any;
  name?: string;

  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;

  uploadingFiles: string[];
  setUploadingFiles: React.Dispatch<React.SetStateAction<string[]>>;

  uploadProgress: Record<string, number>;
  setUploadProgress: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;

  imageLoadingStates: Record<string, boolean>;
  setImageLoadingStates: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

interface LoadingStates {
  [key: string]: boolean;
}

const ImageUpload: React.FC<FileUploadProps> = ({
  maxFiles = 10,
  maxSizeMB = 50,
  acceptedFormats = [
    "image/jpeg",
    "image/png",
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/mov",
  ],
  onFilesSelected,
  onUploadComplete,
  additionalInfo,
  setAdditionalInfo,
  additionalInfoView,
  uploadedUrls,
  setUploadedUrls,
  setValue,
  name,
  files,
  setFiles,

  uploadingFiles,
  setUploadingFiles,

  uploadProgress,
  setUploadProgress,

  imageLoadingStates,
  setImageLoadingStates,
}) => {
  console.log("Uploading Files", uploadingFiles);
  const [isDragging, setIsDragging] = useState(false);

  const [processingMessage, setProcessingMessage] = useState<string>("");

  // useEffect(() => {
  //   if (setValue && name) {
  //     setValue(name, filesData);
  //   }
  // }, [filesData, setValue, name]);

  const handleImageLoad = (url: string) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [url]: false,
    }));
  };

  const handleAdditionalInfoChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setAdditionalInfo(e.target.value);
  };

  const generateFileKey = (file: File) => {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split(".").pop();
    return `properties/${timestamp}-${randomString}.${extension}`;
  };
  const uploadWithProgress = (
    uploadUrl: string,
    file: File,
    onProgress: (percent: number) => void,
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);

          onProgress(percent);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", uploadUrl);

      xhr.setRequestHeader("Content-Type", file.type);

      xhr.send(file);
    });
  };

  const uploadFile = async (file: File) => {
    try {
      const key = generateFileKey(file);
      setUploadingFiles((prev) => [...prev, file.name]);

      let fileToUpload: File | Blob = file;

      setProcessingMessage("Uploading file...");
      const { data } = await PropertyService.GetUploadUrl(key);
      const { upload_url, metadata } = data?.data;

      await uploadWithProgress(upload_url, file, (percent) => {
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: percent,
        }));
      });

      setUploadingFiles((prev) => prev.filter((f) => f !== file.name));
      setImageLoadingStates((prev) => ({
        ...prev,
        [metadata?.url]: true,
      }));
      setProcessingMessage("");
      return metadata?.url;
    } catch (error) {
      console.error("Error uploading file:", error);
      setUploadingFiles((prev) => prev.filter((f) => f !== file.name));
      setProcessingMessage("");
    } finally {
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[file.name];
        return next;
      });
    }
  };

  const handleFiles = useCallback(
    async (newFiles: FileList | null) => {
      if (!newFiles) return;

      const validFiles: File[] = [];

      Array.from(newFiles).forEach((file) => {
        if (!acceptedFormats.includes(file.type)) {
          toast.info(`File type ${file.type} is not supported`);
          return;
        }

        if (file.size > maxSizeMB * 1024 * 1024) {
          toast.info(`File ${file.name} is larger than ${maxSizeMB}MB`);
          return;
        }

        if (files.length + validFiles.length >= maxFiles) {
          toast.info(`Maximum ${maxFiles} files allowed`);
          return;
        }

        validFiles.push(file);
      });

      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      onFilesSelected?.(validFiles);

      try {
        const uploadPromises = validFiles.map((file) => uploadFile(file));
        const newUploadedUrls = await Promise.all(uploadPromises);
        const filteredUrls = newUploadedUrls.filter(
          (url): url is string => url !== undefined,
        );
        setUploadedUrls((prev: string[]) => [...prev, ...filteredUrls]);
        onUploadComplete?.([...uploadedUrls, ...filteredUrls]);
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.info("Error uploading files. Please try again.");
      }
    },
    [
      files,
      maxFiles,
      maxSizeMB,
      acceptedFormats,
      onFilesSelected,
      uploadedUrls,
      onUploadComplete,
    ],
  );

  const removeFile = useCallback(
    (index: number) => {
      const fileToRemove = files[index];
      const urlToRemove = uploadedUrls[index];

      setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
      setUploadedUrls((prevUrls: string[]) =>
        prevUrls.filter((_, i) => i !== index),
      );
      setUploadingFiles((prev) => prev.filter((f) => f !== fileToRemove.name));
      setImageLoadingStates((prev) => {
        const newState = { ...prev };
        delete newState[urlToRemove];
        return newState;
      });
      onUploadComplete?.(uploadedUrls.filter((_, i) => i !== index));
    },
    [files, uploadedUrls, onUploadComplete],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <div className="w-full border-b border-soft-200 mb-4 pb-8">
      {additionalInfoView && (
        <FormInput
          name="additionalInfo"
          value={additionalInfo}
          label="Additional info"
          placeholder="Type something brief..."
          className="w-full"
          onChange={handleAdditionalInfoChange}
          isTextArea={true}
        />
      )}

      <p className="text-sm font-medium text-main-900 mb-4">
        Upload property pictures <span className="text-red-500 text-sm">*</span>
      </p>

      <div
        className={`relative border border-dashed rounded-lg p-8 text-center
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"}
          hover:border-blue-500 transition-colors duration-200`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept={acceptedFormats.join(",")}
          onChange={(e) => handleFiles(e.target.files)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div className="ml-2 flex flex-col gap-3 w-full bg-gray-100">
          {uploadingFiles.map((fileName) => {
            const progress = uploadProgress[fileName] ?? 0;
            const file = files.find((f) => f.name === fileName);
            const fileSize = file ? formatFileSize(file.size) : "";

            return (
              <div
                key={fileName}
                className="flex flex-col gap-1 w-full p-4 rounded-sm border border-gray-300"
              >
                {/* File Name & Text Percentage */}
                <div className="flex justify-between items-center text-md text-gray-500 font-medium">
                  <span className="truncate">
                    {fileName}
                    {fileSize && (
                      <span className="ml-1 text-gray-400 font-normal">
                        ({fileSize})
                      </span>
                    )}
                  </span>
                  <span className="font-semibold text-bnp-cyan">
                    {progress}%
                  </span>
                </div>

                {/* Progress Bar Track */}
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  {/* Active Progress Fill */}
                  <div
                    className="h-full bg-bnp-cyan rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="flex justify-center">
            <svg
              className="w-12 h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              Choose a file or drag & drop it here
            </p>
            <p className="text-sm text-gray-500">
              {acceptedFormats
                .map((format) => format.split("/")[1].toUpperCase())
                .join(", ")}
              &nbsp;formats, up to {maxSizeMB} MB
            </p>
          </div>
        </div>
      </div>

      {uploadedUrls.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {uploadedUrls.map((url, index) => (
            <div key={url} className="relative group">
              <div className="relative w-full h-40">
                {imageLoadingStates[url] && (
                  <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                )}
                {isVideoFile(url) ? (
                  <video
                    src={url}
                    className={`w-full h-40 object-cover rounded-lg transition-opacity duration-300 ${
                      imageLoadingStates[url] ? "opacity-0" : "opacity-100"
                    }`}
                    controls
                    muted
                    autoPlay
                    playsInline
                    onLoadedData={() => handleImageLoad(url)} // Correct event handler target for video media
                  />
                ) : (
                  <img
                    src={url}
                    alt={`Uploaded ${index + 1}`}
                    className={`w-full h-40 object-cover rounded-lg transition-opacity duration-300 ${
                      imageLoadingStates[url] ? "opacity-0" : "opacity-100"
                    }`}
                    onLoad={() => handleImageLoad(url)} // Correct event handler target for standard images
                  />
                )}
              </div>
              <button
                onClick={() => removeFile(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1
                  opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
